/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { ApplicationLifetimeService } from 'application-lifetime/lib/application-lifetime.service';
import { UIShutdownReason } from 'application-lifetime/lib/application-lifetime';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { WindowService } from 'crimson/lib/window/window.service';
import { dispatchKeyboardEvent, getSpatialNavigationEventTarget } from 'utils/lib/web-events/synthetic-events';
import { isTizenAPIAvailable } from 'utils/lib/platform/tizen';
import { KeyboardEventBackKey, KeyboardEventType } from 'utils/lib/web-events/keyboard-event';

@Injectable({
    providedIn: 'root'
})
export class BackNavigationService {
    private readonly logger: Logger;
    private readonly window: Window;
    private backToExitAppEnabled = false;

    constructor(
        loggingService: LoggingService,
        windowService: WindowService,
        private ngZone: NgZone,
        private applicationLifetimeService: ApplicationLifetimeService
    ) {
        this.logger = loggingService.getLogger('BackNavigationService');
        this.window = windowService.window;
    }

    /**
     * Enables {@link KeyboardEventBackKey} to exit the application.
     * Note that the underlying back-to-exit handler would be the last back handler in the responding chain,
     * which means that back-to-exit will happen only when no other component handles {@link KeyboardEventBackKey}.
     *
     * Under the hood, the handler uses {@link ApplicationLifetimeService} to exit the application, giving the client
     * a chance to interrupt by implementing its shutdown work.
     */
    enableBackToExitApp() {
        if (this.backToExitAppEnabled) {
            return;
        }

        this.logger.info('Enabling back to exit app');

        this.ngZone.runOutsideAngular(() => {
            this.window.addEventListener('keydown', this.backToExitAppListener);
        });
        this.backToExitAppEnabled = true;
    }

    /**
     * Disables {@link KeyboardEventBackKey} to exit the application.
     */
    disableBackToExitApp() {
        if (!this.backToExitAppEnabled) {
            return;
        }

        this.logger.info('Disabling back to exit app');
        this.window.removeEventListener('keydown', this.backToExitAppListener);
        this.backToExitAppEnabled = false;
    }

    private readonly backToExitAppListener = (event: KeyboardEvent) => {
        // Do nothing if the key was not an escape/back key, or the event was prevented (i.e. a component had consumed the event)
        if ((event.key !== 'Escape' && event.key !== 'GoBack') || event.defaultPrevented) {
            return;
        }

        this.ngZone.run(() => {
            this.logger.info('Requesting application exit');
            this.applicationLifetimeService.shutdownApplication(UIShutdownReason.ApplicationRequestedExit);
        });
    };

    /**
     * Translates system back event to the specified back key.
     *
     * System back is usually delivered through a platform-specific event.
     * This method listens to platform-specific back event and translates it into the ones understood by back navigation.
     *
     * @param backKey the back key which system back will be translated to.
     */
    translateSystemBackTo(backKey: KeyboardEventBackKey) {
        this.logger.info('System back will be translated to', backKey);

        this.translateTizenBackTo(backKey);
        this.translateShiftedEscapeTo(backKey);
        this.translateVegaGoBackTo(backKey);
    }

    private translateTizenBackTo(backKey: KeyboardEventBackKey) {
        if (!isTizenAPIAvailable()) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.window.addEventListener('tizenhwkey', (event: any) => {
                if (event.keyName === 'back') {
                    const target = getSpatialNavigationEventTarget();
                    dispatchKeyboardEvent(target, 'keydown', backKey);
                    dispatchKeyboardEvent(target, 'keyup', backKey);
                }
            });
        });
    }

    // Angular android sends shifted Escape on back pressed
    private translateShiftedEscapeTo(backKey: KeyboardEventBackKey) {
        // Note that we deliberately translate ShiftEscape to Escape because Angular's event binding
        // doesn't fire `keydown.escape` on ShiftEscape (only firing `keydown.shift.escape`).

        const replaceShiftedEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && event.shiftKey) {
                event.preventDefault();
                event.stopImmediatePropagation();
                dispatchKeyboardEvent(event.target, event.type as KeyboardEventType, backKey);
            }
        };

        this.ngZone.runOutsideAngular(() => {
            this.window.addEventListener('keydown', replaceShiftedEscape, { capture: true });
            this.window.addEventListener('keyup', replaceShiftedEscape, { capture: true });
        });
    }

    // On Vega OS (Fire TV), the remote's Back button is delivered to web JS as a regular keydown
    // with `event.key === 'GoBack'` (requires `allowSystemKeyEvents={true}` on the WebView shell).
    // Translate it to the requested back key so existing Escape-based listeners fire uniformly.
    // Gating is handled by the caller via the `translateSystemBackTo` config flag.
    private translateVegaGoBackTo(backKey: KeyboardEventBackKey) {
        if (backKey === 'GoBack') {
            return;
        }

        const replaceGoBack = (event: KeyboardEvent) => {
            if (event.key === 'GoBack') {
                event.preventDefault();
                event.stopImmediatePropagation();
                dispatchKeyboardEvent(event.target, event.type as KeyboardEventType, backKey);
            }
        };

        this.ngZone.runOutsideAngular(() => {
            this.window.addEventListener('keydown', replaceGoBack, { capture: true });
            this.window.addEventListener('keyup', replaceGoBack, { capture: true });
        });
    }
}
